"use client";

import { useEffect, useMemo, useState } from "react";
import { BellRing, Clock3, ReceiptText, ShoppingBag, Star } from "lucide-react";

import { cn } from "@/components/ui/utils";
import { usePlaceOptional } from "@/components/place/PlaceProvider";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import type { TemplateSurfacePalette } from "@/app/dashboard/templates/components/lib/templateExperience";

type PublicResolvedTableSession = {
 table: {
 id: string;
 label: string;
 order_mode: "service_console";
 is_counter_pickup?: boolean;
 };
 session: {
 session_code: string;
 expires_at: string;
 };
 seatLabel?: string | null;
};

type Props = {
 publicCode?: string | null;
 session: PublicResolvedTableSession | null;
 error?: string | null;
 isResolving: boolean;
 isCounterPickup: boolean;
 orderingEnabled: boolean;
 counterRequiresEntryCode: boolean;
 counterRequiresGuestName: boolean;
 activeServiceRequest: string | null;
 serviceRequestsEnabled?: boolean;
 palette: TemplateSurfacePalette;
 onOrder: () => void;
 onFeedback: () => void;
 onServiceRequest: (requestType: "call_waiter" | "request_bill") => void;
};

export function PublicSessionStatusStrip({
 publicCode,
 session,
 error,
 isResolving,
 isCounterPickup,
 orderingEnabled,
 counterRequiresEntryCode,
 counterRequiresGuestName,
 activeServiceRequest,
 serviceRequestsEnabled = true,
 palette,
 onOrder,
 onFeedback,
 onServiceRequest,
}: Props) {
 // Inside a Place shell, the session ribbon already states the table and what
 // the guest's taps do, and the bottom rail already carries Order. Repeating
 // both here gave a seated guest three places to start an order and two
 // statements of where they were sitting — the "everything feels misplaced"
 // complaint reproducing itself inside one screen. What is NOT duplicated —
 // Feedback, Call staff, Bill, and the session countdown — stays.
 const insidePlace = Boolean(usePlaceOptional());

 // Live countdown to session expiry
 const [now, setNow] = useState(() => Date.now());
 useEffect(() => {
 if (!session) return;
 const id = setInterval(() => setNow(Date.now()), 10_000);
 return () => clearInterval(id);
 }, [session]);

 const countdown = useMemo(() => {
 if (!session) return null;
 const expiresAt = new Date(session.session.expires_at).getTime();
 const diffMs = expiresAt - now;
 if (diffMs <= 0) return { label: "Expired", urgent: true };
 const totalMin = Math.floor(diffMs / 60_000);
 if (totalMin >= 60) {
 const h = Math.floor(totalMin / 60);
 const m = totalMin % 60;
 return { label: `${h}h ${m}m left`, urgent: false };
 }
 if (totalMin < 5) return { label: `${totalMin}m left`, urgent: true };
 return { label: `${totalMin}m left`, urgent: false };
 }, [session, now]);
  if (!publicCode && !session && !error) {
    return null;
  }

 return (
 <section className="mx-auto w-full max-w-[980px] px-3 pb-3 pt-3 sm:px-4 sm:pb-4 sm:pt-4">
 <div
 className="rounded-[var(--pub-r-md)] border px-3 py-3 shadow-[var(--mnrq-shell-shadow)] sm:px-4"
 style={{
 borderColor: palette.border,
 backgroundColor: palette.card,
 color: palette.cardForeground,
 }}
 >
 {session ? (
 <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
 <div className="min-w-0">
 <div className="flex flex-wrap items-center gap-2">
 {/* The mode chip and the table label are exactly what the session ribbon
     says one row above, so inside a Place they are dropped. The session
     CODE stays — a guest reads it aloud to staff and it appears nowhere
     else. */}
 {insidePlace ? null : (
 <span className="rounded-full border px-2.5 py-1 text-2xs font-semibold uppercase tracking-[0.12em]" style={{ borderColor: palette.border, backgroundColor: palette.muted, color: palette.mutedForeground }}>
 {isCounterPickup ? "Counter pickup" : "Dine-in"}
 </span>
 )}
 {insidePlace ? null : (
 <span className="truncate text-sm font-semibold" style={{ color: palette.cardForeground }}>
 {isCounterPickup ? "Counter pickup" : session.table.label}
 {!isCounterPickup && session.seatLabel ? ` · Seat ${session.seatLabel}` : ""}
 </span>
 )}
 <span className="font-mono text-xs" style={{ color: palette.mutedForeground }}>
 {session.session.session_code}
 </span>
 </div>
 <p className="mt-1 text-xs" style={{ color: palette.mutedForeground }}>
 {isCounterPickup
 ? orderingEnabled
 ? `Pickup ordering ${counterRequiresEntryCode ? "requires entry code" : "has optional entry code"} · ${counterRequiresGuestName ? "name required" : "name optional"}`
 : "Pickup ordering is off. Menu browsing is available."
 : orderingEnabled
 ? serviceRequestsEnabled
 ? "Orders and service requests are linked to this table."
 : "Orders are linked to this table."
 : "Ordering is off. Menu browsing is available."}
 </p>
 </div>

 <div className="relative flex gap-2 overflow-x-auto pb-1 text-sm md:pb-0" style={{ color: palette.mutedForeground }}>
 {/* Fade gradient hint for scrollable overflow */}
 <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-[var(--color-bg-surface,white)] to-transparent" aria-hidden="true" />
 {isCounterPickup ? (
 <Button
 type="button"
 size="sm"
 className="shrink-0 rounded-full"
 disabled={!orderingEnabled}
 style={{ backgroundColor: orderingEnabled ? palette.accent : palette.muted, color: orderingEnabled ? palette.accentForeground : palette.mutedForeground }}
 onClick={onOrder}
 >
 <ShoppingBag className="h-4 w-4" />
 {orderingEnabled ? "Order pickup" : "Read only"}
 </Button>
 ) : (
 <>
 <Button type="button" variant="outline" size="sm" className="shrink-0 justify-start rounded-full" style={{ borderColor: palette.border, backgroundColor: palette.muted, color: palette.mutedForeground }} onClick={onFeedback}>
 <Star className="h-4 w-4" />
 Feedback
 </Button>
 {serviceRequestsEnabled
 ? [
 { type: "call_waiter" as const, label: "Call staff", icon: BellRing },
 { type: "request_bill" as const, label: "Bill", icon: ReceiptText },
 ].map((entry) => {
 const Icon = entry.icon;
 return (
 <Button key={entry.type} type="button" variant="outline" size="sm" className="shrink-0 justify-start rounded-full" style={{ borderColor: palette.border, backgroundColor: palette.muted, color: palette.mutedForeground }} disabled={activeServiceRequest === entry.type} onClick={() => onServiceRequest(entry.type)}>
 <Icon className="h-4 w-4" />
 {activeServiceRequest === entry.type ? "Sent ✓" : entry.label}
 </Button>
 );
 })
 : null}
 {orderingEnabled && !insidePlace ? (
 <Button type="button" size="sm" className="shrink-0 rounded-full" style={{ backgroundColor: palette.accent, color: palette.accentForeground }} onClick={onOrder}>
 <ShoppingBag className="h-4 w-4" />
 Order
 </Button>
 ) : null}
 </>
 )}
 <div className={cn("inline-flex shrink-0 items-center gap-2 rounded-full border px-3 py-1.5", countdown?.urgent && "border-[var(--color-danger)]/20 bg-[var(--color-danger-soft)]")} style={{ borderColor: countdown?.urgent ? undefined : palette.border, backgroundColor: countdown?.urgent ? undefined : palette.muted }}>
 <Clock3 className="h-4 w-4" style={{ color: countdown?.urgent ? "rgb(220 38 38)" : palette.mutedForeground }} />
 <span className={cn(countdown?.urgent && "text-[var(--color-danger)] font-medium")}>
 {countdown?.label || "Loading..."}
 </span>
 </div>
 </div>
 </div>
 ) : error ? (
 <div className="space-y-2">
 <p className="text-2xs font-semibold uppercase tracking-[0.18em]" style={{ color: palette.mutedForeground }}>
 QR session unavailable
 </p>
 <p className="text-sm" style={{ color: palette.mutedForeground }}>
 {error} Scan the current QR code again if this table or counter label has changed.
 </p>
 <Button
 type="button"
 size="sm"
 variant="outline"
 className="mt-1"
 style={{ borderColor: palette.border, backgroundColor: palette.muted, color: palette.mutedForeground }}
 onClick={() => window.location.reload()}
 >
 Retry
 </Button>
 </div>
 ) : (
 <div className="flex items-center gap-3 text-sm" style={{ color: palette.mutedForeground }}>
 <Skeleton className="h-4 w-4 shrink-0 rounded-full" style={{ backgroundColor: `${palette.accent}30` }} />
 <span>{isResolving ? "Connecting this menu to your QR session..." : "Resolving QR session..."}</span>
 </div>
 )}
 </div>
 </section>
 );
}
